import { zodResolver } from "@hookform/resolvers/zod";
import {
  createPatientSchema,
  GENDERS,
  missingProfileFields,
  type ProfileCompletionField,
  type CreatePatientInput,
  type PatientClinicalView,
} from "@clinic/shared";
import { useEffect, type JSX } from "react";
import { Controller, useForm } from "react-hook-form";
import { revealFirstError } from "@web/shared/lib/form-errors";
import { WhatsAppPhoneInput } from "@web/shared/components/whatsapp-phone-input";
import { useTranslation } from "react-i18next";
import {
  Button,
  DatePicker,
  FormField,
  Icon,
  Input,
  Modal,
  PhoneInput,
  Select,
  usePersonName,
  useToast,
} from "@clinic/ui";
import { doctorOptionLabel } from "@web/shared/lib/doctor-label";
import { useDoctors } from "@web/shared/queries/doctors";
import { useCreatePatient, useUpdatePatient } from "@web/modules/patients/queries";
import { errorToast } from "@web/shared/lib/api-error";
import { ellipsis } from "@web/i18n/ellipsis";
import { formatList } from "@web/shared/lib/format";
import { todayIso } from "@web/shared/lib/dates";
import { fieldNeeds } from "@web/shared/lib/schema-fields";

const need = fieldNeeds(createPatientSchema);

interface PatientFormModalProps {
  "data-testid"?: string | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: ((patientId: string) => void) | undefined;
  patient?: PatientClinicalView | undefined;
}

export function PatientFormModal({
  open,
  onOpenChange,
  onCreated,
  patient,
  "data-testid": testId = "patient-form-modal",
}: PatientFormModalProps): JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const editing = patient !== undefined;
  const createPatient = useCreatePatient();
  const updatePatient = useUpdatePatient(patient?.id ?? "");
  const displayName = usePersonName();
  const doctors = useDoctors({ limit: 100 });
  const doctorChoices = (doctors.data?.items ?? [])
    .filter((doctor) => doctor.user.isActive || doctor.id === patient?.assignedDoctorId)
    .map((doctor) => ({
      value: doctor.id,
      label: doctorOptionLabel(doctor, displayName(doctor.user.name), t),
    }));

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isValid },
    control,
    watch,
    getValues,
    setValue,
  } = useForm<CreatePatientInput>({
    mode: "onTouched",
    resolver: zodResolver(createPatientSchema),
  });
  const onlyDoctor = doctorChoices.length === 1 ? doctorChoices[0]?.value : undefined;

  const missing = editing
    ? missingProfileFields({ dateOfBirth: watch("dateOfBirth"), gender: watch("gender") })
    : [];
  const needed = (field: ProfileCompletionField): boolean => missing.includes(field);

  useEffect(() => {
    if (!open) {
      return;
    }

    reset(
      patient
        ? {
            firstName: patient.firstName,
            middleName: patient.middleName,
            lastName: patient.lastName,
            phone: patient.phone,
            whatsapp: patient.whatsapp,
            dateOfBirth: patient.dateOfBirth,
            gender: patient.gender,
            address: patient.address,
            emergencyContactName: patient.emergencyContactName,
            emergencyContactPhone: patient.emergencyContactPhone,
            assignedDoctorId: patient.assignedDoctorId,
          }
        : { firstName: "", middleName: "", lastName: "" },
    );
  }, [open, patient, reset]);

  useEffect(() => {
    if (open && onlyDoctor !== undefined && !getValues("assignedDoctorId")) {
      setValue("assignedDoctorId", onlyDoctor, { shouldDirty: true, shouldValidate: true });
    }
  }, [open, onlyDoctor, getValues, setValue]);

  const onSubmit = handleSubmit(
    async (values) => {
      try {
        if (editing) {
          await updatePatient.mutateAsync(values);
          toast.success("patients.updated");
          onOpenChange(false);
          return;
        }

        const created = await createPatient.mutateAsync(values);
        toast.success("patients.created");
        onOpenChange(false);
        onCreated?.(created.id);
      } catch (error) {
        toast.error(...errorToast(error));
      }
    },
    () => revealFirstError(),
  );

  return (
    <Modal
      data-testid={testId}
      open={open}
      onOpenChange={onOpenChange}
      title={editing ? "patients.edit" : "patients.create"}
      size="lg"
      footer={
        <>
          <Button
            icon={<Icon name="x" />}
            variant="secondary"
            data-testid={`${testId}-cancel`}
            onClick={() => onOpenChange(false)}
          >
            {t("common.cancel")}
          </Button>
          <Button
            icon={<Icon name="check" />}
            type="submit"
            aria-disabled={!isValid || isSubmitting || undefined}
            form="patient-form"
            data-testid={`${testId}-save`}
            isLoading={isSubmitting}
          >
            {isSubmitting ? ellipsis(t("common.saving")) : t("common.save")}
          </Button>
        </>
      }
    >
      <form
        id="patient-form"
        data-testid={`${testId}-form`}
        className="flex flex-col gap-4"
        onSubmit={onSubmit}
        noValidate
      >
        {missing.length > 0 && (
          <p
            data-testid={`${testId}-incomplete`}
            role="status"
            className="flex items-start gap-2 rounded-panel bg-warning-50 px-3 py-2 text-label text-warning-800"
          >
            <Icon name="alert" className="mt-0.5 size-4 shrink-0" />
            {t("patients.incompleteMissing", {
              fields: formatList(missing.map((field) => t(`patients.${field}`))),
            })}
          </p>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            label="patients.firstName"
            {...need("firstName")}
            htmlFor="patient-first-name"
            error={errors.firstName}
          >
            <Input
              placeholder={t("common.placeholders.firstName")}
              adornment="user"
              id="patient-first-name"
              data-testid="patient-field-first-name"
              hasError={Boolean(errors.firstName)}
              {...register("firstName")}
            />
          </FormField>

          <FormField
            label="patients.lastName"
            {...need("lastName")}
            htmlFor="patient-last-name"
            error={errors.lastName}
          >
            <Input
              placeholder={t("common.placeholders.lastName")}
              id="patient-last-name"
              data-testid="patient-field-last-name"
              hasError={Boolean(errors.lastName)}
              {...register("lastName")}
            />
          </FormField>

          <FormField
            label="patients.middleName"
            {...need("middleName")}
            htmlFor="patient-middle-name"
            error={errors.middleName}
          >
            <Input
              placeholder={t("common.placeholders.middleName")}
              id="patient-middle-name"
              data-testid="patient-field-middle-name"
              hasError={Boolean(errors.middleName)}
              {...register("middleName")}
            />
          </FormField>

          <FormField
            label="patients.phone"
            {...need("phone")}
            htmlFor="patient-phone"
            error={errors.phone}
            errorKey={errors.phone ? "errors.validation.invalidPhone" : undefined}
          >
            <Controller
              name="phone"
              control={control}
              render={({ field }) => (
                <WhatsAppPhoneInput
                  check={!getValues("whatsapp")}
                  placeholder={t("common.placeholders.phone")}
                  id="patient-phone"
                  data-testid="patient-field-phone"
                  hasError={Boolean(errors.phone)}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                />
              )}
            />
          </FormField>

          <FormField
            label="patients.dateOfBirth"
            htmlFor="patient-dob"
            error={errors.dateOfBirth}
            {...(needed("dateOfBirth")
              ? { note: "patients.neededToComplete" }
              : need("dateOfBirth"))}
          >
            <Controller
              control={control}
              name="dateOfBirth"
              render={({ field }) => (
                <DatePicker
                  id="patient-dob"
                  data-testid="patient-field-dob"
                  label={t("patients.dateOfBirth")}
                  value={field.value ?? ""}
                  max={todayIso()}
                  hasError={errors.dateOfBirth !== undefined}
                  onChange={(value) => field.onChange(value === "" ? null : value)}
                />
              )}
            />
          </FormField>

          <FormField
            label="patients.gender"
            htmlFor="patient-gender"
            error={errors.gender}
            {...(needed("gender") ? { note: "patients.neededToComplete" } : need("gender"))}
          >
            <Controller
              name="gender"
              control={control}
              render={({ field }) => (
                <Select
                  id="patient-gender"
                  data-testid="patient-field-gender"
                  placeholder={t("common.none")}
                  options={GENDERS.map((gender) => ({
                    value: gender,
                    label: t(`patients.${gender}`),
                  }))}
                  value={field.value ?? ""}
                  onBlur={field.onBlur}
                  onChange={(event) => field.onChange(event.target.value || null)}
                />
              )}
            />
          </FormField>

          <FormField
            label="patients.assignedDoctor"
            {...need("assignedDoctorId")}
            htmlFor="patient-assigned-doctor"
            error={errors.assignedDoctorId}
            hint="patients.assignedDoctorHint"
          >
            <Controller
              name="assignedDoctorId"
              control={control}
              render={({ field }) => (
                <Select
                  id="patient-assigned-doctor"
                  data-testid="patient-field-assigned-doctor"
                  placeholder={t("common.none")}
                  searchable={doctorChoices.length > 8}
                  disabled={onlyDoctor !== undefined}
                  options={doctorChoices}
                  value={field.value ?? ""}
                  onBlur={field.onBlur}
                  onChange={(event) => field.onChange(event.target.value || null)}
                />
              )}
            />
          </FormField>

          <FormField
            label="patients.address"
            {...need("address")}
            htmlFor="patient-address"
            error={errors.address}
          >
            <Input
              placeholder={t("common.placeholders.address")}
              id="patient-address"
              data-testid="patient-field-address"
              {...register("address", { setValueAs: (value) => (value === "" ? null : value) })}
            />
          </FormField>

          <FormField
            label="patients.emergencyContactName"
            {...need("emergencyContactName")}
            htmlFor="patient-emergency-name"
            error={errors.emergencyContactName}
          >
            <Input
              placeholder={t("common.placeholders.emergencyName")}
              adornment="user"
              id="patient-emergency-name"
              data-testid="patient-field-emergency-name"
              {...register("emergencyContactName", {
                setValueAs: (value) => (value === "" ? null : value),
              })}
            />
          </FormField>

          <FormField
            label="patients.emergencyContactPhone"
            {...need("emergencyContactPhone")}
            htmlFor="patient-emergency-phone"
            error={errors.emergencyContactPhone}
          >
            <Controller
              name="emergencyContactPhone"
              control={control}
              render={({ field }) => (
                <PhoneInput
                  placeholder={t("common.placeholders.phone")}
                  id="patient-emergency-phone"
                  data-testid="patient-field-emergency-phone"
                  hasError={Boolean(errors.emergencyContactPhone)}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                />
              )}
            />
          </FormField>
        </div>
      </form>
    </Modal>
  );
}
